
import type { ChangeEvent, Dispatch, SetStateAction, KeyboardEvent } from 'react'

import { useEffect, useRef } from 'react'
import { MdHighlightOff, MdSend } from 'react-icons/md'

import { Divider } from '../../shared/Divider'

import { PDFIcon } from './icons/PDFIcon'
import { TextInput } from './text-input'

function formatBytes (bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes: string[] = [ 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB' ]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  const sizeName: string | undefined = sizes[i]

  if (sizeName == null) {
    return '0'
  }

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizeName}`
}

interface CancelButtonProps {
  onClick: () => void
}

const CancelButton = ({ onClick }: CancelButtonProps): JSX.Element => {
  return (
    <div className='buttons'>
      <button
        type='button'
        data-cy='model_cancel-attach-btn'
        title='Detach File'
        onClick={onClick}
      >
        <MdHighlightOff size='1.3rem' />
      </button>
    </div>
  )
}

interface MessageAttachmentModalProps {
  preview: string | null
  value: string
  onCancel: () => void
  setSending: Dispatch<SetStateAction<boolean>>
  onKeyPress: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  onKeyUp: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  placeholder: string
  onSendMessage: (value: string) => void | Promise<void>
  meta: File | null
}

export function MessageAttachmentModal (
  {
    preview,
    value,
    onCancel,
    setSending,
    onKeyPress,
    onKeyDown,
    onChange,
    onKeyUp,
    placeholder,
    onSendMessage,
    meta
  }: MessageAttachmentModalProps): JSX.Element {
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    inputRef.current?.setSelectionRange(value.length, value.length)
    inputRef.current?.focus()
  }, [ value.length, preview ])

  const disabledButton = (preview == null) && value.length <= 0

  const sendButton = (
    <button
      key={1}
      type='button'
      title='Send Message'
      data-cy='modal_send-btn'
      disabled={disabledButton}
      onClick={() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onSendMessage(value)
      }}
    >
      <MdSend />
    </button>
  )

  const actions = {
    right: [ sendButton ]
  }

  return (
    <div className='attachment_message_modal'>
      <div className='body'>
        <div className='body-preview'>
          {preview === 'pdf'
            ? (
              <div className='pdf'>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <div className='pdf-icon'>
                    <PDFIcon />
                  </div>
                  {meta && (
                    <div className='meta'>
                      <p className='name'>{meta.name}</p>
                      <p className='size'>{formatBytes(meta.size)}</p>
                    </div>
                  )}
                </div>
                <CancelButton onClick={onCancel} />
              </div>
            )
            : (
              (preview !== null) && (
                <div className='image'>
                  <img
                    width='400px'
                    src={preview}
                    alt={meta?.name}
                  />
                  <CancelButton onClick={onCancel} />
                </div>
              )
            )}
        </div>
        <Divider />
        <div className='chat-input'>
          <TextInput
            placeholder={placeholder}
            onChange={onChange}
            value={value}
            onKeyPress={onKeyPress}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            setSending={setSending}
            textInputRef={inputRef}
            actions={actions}
          />
        </div>
      </div>
    </div>
  )
}
